/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Serviço de integração com o @superset-ui/embedded-sdk.
 * Toda a parte de login + geração de guest_token é feita no backend.
 * Este serviço apenas:
 *  - chama o backend para obter o guest_token de um dashboard específico; e
 *  - chama embedDashboard da SDK para montar o iframe em um elemento HTML.
 * =============================================================================
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import { SupersetService, SupersetDashboard } from "./superset.service";
import { loadSupersetSDK } from "./superset-wrapper";

export interface BiIdFilters {
  fromDttm?: string;
  toDttm?: string;
  fromMonth?: string;
  toMonth?: string;
  idEmpresa?: number[];
  idDivisao?: number[];
  idVeiculo?: number[];
  idTerminal?: number[];
  idMotorista?: number[];
  eventName?: string[];
}

@Injectable({
  providedIn: "root",
})
export class SupersetEmbedService {
  private currentInstance: { unmount: () => void } | null = null;

  constructor(private supersetService: SupersetService) {}

  /**
   * Faz o embed de um dashboard Superset no elemento informado.
   * Usa o embed_id (UUID configurado no popup de Embed) e busca o guest_token no backend.
   */
  embedDashboard(
    dashboard: SupersetDashboard,
    mountPoint: HTMLElement,
    filters?: BiIdFilters,
  ): Observable<void> {
    return new Observable<void>((observer) => {
      if (!dashboard?.embed_id) {
        observer.error(
          new Error(
            "Dashboard não possui embed_id configurado. Habilite o embed no Superset e configure o embed_id no backend.",
          ),
        );
        return;
      }

      this.supersetService
        .getDashboardToken(dashboard.id)
        .pipe(take(1))
        .toPromise()
        .then(async (resp) => {
          const sdk = await loadSupersetSDK();

          // desmonta instância anterior, se houver
          this.currentInstance?.unmount?.();

          const urlParams: Record<string, string> = {};
          if (filters?.fromDttm) {
            urlParams.from_dttm = filters.fromDttm;
          }
          if (filters?.toDttm) {
            urlParams.to_dttm = filters.toDttm;
          }
          if (filters?.fromMonth) {
            urlParams.from_month = filters.fromMonth;
          }
          if (filters?.toMonth) {
            urlParams.to_month = filters.toMonth;
          }
          if (filters?.idEmpresa?.length) {
            urlParams.id_empresa = filters.idEmpresa.join(",");
          }
          if (filters?.idDivisao?.length) {
            urlParams.id_divisao = filters.idDivisao.join(",");
          }
          if (filters?.idVeiculo?.length) {
            urlParams.id_veiculo = filters.idVeiculo.join(",");
          }
          if (filters?.idTerminal?.length) {
            urlParams.id_terminal = filters.idTerminal.join(",");
          }
          if (filters?.idMotorista?.length) {
            urlParams.id_motorista = filters.idMotorista.join(",");
          }
          if (filters?.eventName?.length) {
            urlParams.event_name = filters.eventName.join(",");
          }

          this.currentInstance = await sdk.embedDashboard({
            id: dashboard.embed_id!,
            supersetDomain: resp.supersetBaseUrl,
            mountPoint,
            fetchGuestToken: async () => resp.guestToken, // agora retorna Promise<string>
            dashboardUiConfig: {
              hideTitle: true,
              hideChartControls: true,
              hideTab: true,
              filters: {
                visible: false,
                expanded: false,
              },
              urlParams: Object.keys(urlParams).length ? urlParams : undefined,
            },
          });

          observer.next();
          observer.complete();
        })
        .catch((err) => {
          console.error("Erro ao obter guest token ou montar embed:", err);
          observer.error(err);
        });
    });
  }

  /**
   * Remove o iframe atual (se existir).
   */
  unmount(): void {
    this.currentInstance?.unmount?.();
    this.currentInstance = null;
  }
}
